import { useState } from 'react';
import styled from '@emotion/styled';
import { Space, Divider, Button, Tooltip, Modal } from 'antd';
import { BotUsageSummary, getBotUsageSummary } from 'services/api/aiReplyService';
import { useMount } from 'ahooks';
import { useAuthorization } from 'components/Authorization';
import { formatNumber } from 'utils/numberFormat';
import { useTranslate } from '@tolgee/react';
import isServiceUnlimited from 'utils/isServiceUnlimited';
import { cx } from '@emotion/css';

import aiServiceImg from '../assets/ai-service.webp';
import AIServiceRecords from './AIServiceRecords';
import EnterpriseUnlimitedTag from './EnterpriseUnlimitedTag';
import { OpenOrderLightWeightModalButton, OpenTrafficOrderModalButton } from './OrderAI/Button';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, map, find, reduce, filter, reverse } from 'lodash-es';
import { orgConfig, updateOrgConfig } from 'services/api/admin';
import { InfoCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import StatusSwitch from '../../../../components/StatusSwitch';
import { PackageCard } from './PackageCard';
import dayjs from 'dayjs';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useLightWeightPackageInfo } from 'hooks/business/user/useLightWeightPackageInfo';
import { queries } from 'services/queries';

const AIServiceOverview = (props: { simple?: boolean }) => {
  const { simple = false } = props;
  const [drawerVisible, setDrawerVisible] = useState(false);
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { t } = useTranslate();

  const [usageSummary, setUsageSummary] = useState<BotUsageSummary | undefined>();

  useMount(() => {
    if (payload?.orgId) {
      // 获取服务次数情况
      getBotUsageSummary(payload.orgId)
        .then((data) => {
          setUsageSummary(data);
        })
        .catch(() => {});
    }
  });

  return (
    <Container
      className={cx({
        '!rounded-[0px] !bg-[#f7f9fa] !border-[#f7f9fa]': simple,
      })}
    >
      {!simple && (
        <div className="bg-[#d5e5ff] self-stretch flex items-center rounded-n">
          <img
            src={aiServiceImg}
            alt="AI"
            style={{
              maxWidth: 104,
              height: 'auto',
            }}
          />
        </div>
      )}
      <Main>
        <InfoContainer size={40} align="center">
          <div>
            <Title>{t('market.本月AI参与服务买家数')}</Title>
            {isServiceUnlimited(usageSummary?.sendLimit) ? (
              <EnterpriseUnlimitedTag />
            ) : (
              <span>
                <Num>
                  {t('aiorder_left', {
                    x: formatNumber(
                      (usageSummary?.sendLimit ?? 0) - (usageSummary?.sentNum ?? 0),
                      '0,0',
                    ),
                  })}{' '}
                  /{' '}
                  {t('aiorder_total', {
                    x: formatNumber(usageSummary?.sendLimit ?? 0, '0,0'),
                  })}
                </Num>
              </span>
            )}
          </div>
          <div>
            <Title>{t('market.本月错失服务买家数')}</Title>
            <Num>
              {usageSummary?.abandonedNum ? (
                <b>{formatNumber(usageSummary.abandonedNum, '0,0')}</b>
              ) : (
                0
              )}
            </Num>
          </div>
        </InfoContainer>
        {!simple && <OpenTrafficOrderModalButton />}
        <Divider type="vertical" style={{ marginLeft: -8, marginRight: -8 }} />
        <Button type="text" onClick={() => setDrawerVisible(true)}>
          {t('market.查看服务记录')}
        </Button>
      </Main>
      {/* 查看服务记录弹窗层 */}
      <AIServiceRecords visible={drawerVisible} onClose={() => setDrawerVisible(false)} />
    </Container>
  );
};

export const AILightWeightServiceOverview = (props: { source: string }) => {
  const { source } = props;
  const { t } = useTranslate();
  const { data: pkgs } = useLightWeightPackageInfo();
  const { data } = useQuery(queries.accounts.orgConfig);
  const { qaEnable, whiteListConfig: { LLM } = {} } = useOrgConfigStateValue();

  const queryClient = useQueryClient();
  const orderedPkg = map(get(pkgs, 'data', []));
  const totalOrderNumber = reduce(
    filter(orderedPkg, (e) => e.source !== 'system'),
    (sum, pkg) => sum + pkg.totalOrderNumber,
    0,
  );
  const usedOrderNumber = reduce(
    filter(orderedPkg, (e) => e.source !== 'system'),
    (sum, pkg) => sum + pkg.usedOrderNumber,
    0,
  );
  const systemTotalOrderNumber = find(orderedPkg, { source: 'system' })?.totalOrderNumber ?? 0;
  const systemUsedOrderNumber = find(orderedPkg, { source: 'system' })?.usedOrderNumber ?? 0;
  const systemEndTime = dayjs(find(orderedPkg, { source: 'system' })?.endTimeStamp ?? 0).format(
    'YYYY-MM-DD',
  );

  if (!LLM || qaEnable) return null;

  return (
    <div className={cx('mt-4 rounded border border-solid border-fill-200')}>
      <ClientBar>
        <span className="flex items-center gap-[4px]">
          {t('AI精准回复能力护航的订单数')}
          <Tooltip
            overlayClassName="[&_.ant-tooltip-content]:!w-[max-content]"
            title={
              <div className="w-[540px]">
                <div className="font-medium">{t('什么是 AI 精准回复能力护航的订单数')}</div>
                <div>{t('AI精准回复说明')}</div>
                <div className="font-medium">{t('与 AI 智能跟单的区别')}</div>
                <div>{t('AI智能跟单说明')}</div>
              </div>
            }
          >
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
        <div className="flex items-center gap-[16px]">
          <div className="flex items-center gap-[8px]">
            <span className="!font-normal">{t('AI精准回复\n')}</span>
            <StatusSwitch
              checked={get(data, 'enableAI')}
              onChange={(enable) => {
                if (!enable) {
                  return new Promise((resolve, reject) => {
                    Modal.confirm({
                      title: t('确认要关闭吗？'),
                      content: t('关闭后，将停止AI精准回复'),
                      onOk: () => {
                        return updateOrgConfig({ ...data, enableAI: enable })
                          .then(() => {
                            queryClient.invalidateQueries(queries.accounts.orgConfig.queryKey);
                            resolve(null);
                          })
                          .catch(reject);
                      },
                      onCancel: () => {
                        resolve(null);
                      },
                    });
                  });
                } else {
                  return updateOrgConfig({ ...orgConfig, enableAI: enable }).then(() => {
                    queryClient.invalidateQueries(queries.accounts.orgConfig.queryKey);
                  });
                }
              }}
            />
          </div>
          <OpenOrderLightWeightModalButton source={source} />
        </div>
      </ClientBar>
      <div className="px-[16px] py-[12px]">
        <div className="bg-[#f7f9fa]">
          <Main>
            <InfoContainer size={40} align="center" className="!flex-none">
              <div>
                <Title>{t('AI精准回复能力护航的订单数（赠送）')}</Title>
                <span>
                  <Num>
                    {t('aiorder_left', {
                      x: formatNumber(systemTotalOrderNumber - systemUsedOrderNumber, '0,0'),
                    })}{' '}
                    /{' '}
                    {t('aiorder_total', {
                      x: formatNumber(systemTotalOrderNumber, '0,0'),
                    })}
                    {systemTotalOrderNumber > 0 && (
                      <Tooltip
                        title={t('优先消耗赠送部分的订单数，有效期至x', { x: systemEndTime })}
                      >
                        <InfoCircleFilled
                          style={{ display: 'inline', marginLeft: 4, cursor: 'pointer' }}
                        />
                      </Tooltip>
                    )}
                  </Num>
                </span>
              </div>
            </InfoContainer>
            <InfoContainer size={40} align="center" className="!flex-none">
              <div>
                <Title>{t('AI精准回复能力护航的订单数（订购）')}</Title>
                <span>
                  <Num>
                    {t('aiorder_left', {
                      x: formatNumber(totalOrderNumber - usedOrderNumber, '0,0'),
                    })}{' '}
                    /{' '}
                    {t('aiorder_total', {
                      x: formatNumber(totalOrderNumber, '0,0'),
                    })}
                  </Num>
                </span>
              </div>
            </InfoContainer>
          </Main>
        </div>
      </div>
      <PackageDeal>
        {reverse(filter(orderedPkg, (e) => e.source !== 'system')).map((pkg) => {
          return (
            <PackageCard
              key={pkg.id}
              color="blue"
              title={t('AI精准回复能力护航的订单数')}
              orderedPackage={
                {
                  ...pkg,
                  validTimeStart: dayjs(Number(pkg.startTimeStamp)).format('YYYY.MM.DD'),
                  validTimeEnd: dayjs(Number(pkg.endTimeStamp)).format('YYYY.MM.DD'),
                  handleNum: pkg.totalOrderNumber,
                  usedHandleNum: pkg.usedOrderNumber,
                  id: 11,
                  status: pkg.status,
                } as any
              }
              showHandler
            />
          );
        })}
      </PackageDeal>
    </div>
  );
};

export default AIServiceOverview;

const Container = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 78px;
  background: #ffffff;
  border: 1px solid #f0f2f5;
  border-radius: 4px;

  img {
    width: auto;
    height: 100%;
  }
`;

const Main = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 8px;
  padding: 10px 0;
`;
const InfoContainer = styled(Space)`
  display: flex;
  align-items: center;
  flex: 1;
  padding: 0 16px;
`;
const Title = styled.div`
  margin-bottom: 6px;
`;
const Num = styled.div`
  font-weight: 500;
  font-size: 16px;
  color: #232426;

  > b {
    color: #f05b5b;
  }
`;
const ClientBar = styled.div`
  height: 48px;
  background: #f7f9fa;
  display: flex;
  padding: 0 16px;
  align-items: center;
  justify-content: space-between;
  font-weight: 500;
  & > span {
    color: #232426;
    span.anticon {
      color: #515359;
      margin-left: 4px;
    }
  }
`;

const PackageDeal = styled.div`
  display: flex;
  flex-direction: row;
  padding: 4px 16px 0 16px;
  flex-wrap: wrap;
  > div:not(:last-child) {
    margin-right: 18px;
  }
`;
